'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { PageErrorBoundary } from '@/components/ErrorBoundary';

type AuthMode = 'phone' | 'google' | 'email';
type PhoneStep = 'input' | 'otp';

export default function LoginPage() {
  const [mode, setMode] = useState<AuthMode>('phone');
  const [phoneStep, setPhoneStep] = useState<PhoneStep>('input');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  const getSupabase = () => {
    const { createClient } = require('@/lib/supabase/client');
    return createClient();
  };

  const startCooldown = (seconds: number) => {
    setResendCooldown(seconds);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) {
          if (cooldownRef.current) clearInterval(cooldownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError('');
    try {
      const supabase = getSupabase();
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: `${window.location.origin}/dashboard` },
      });
      if (error) throw error;
    } catch (err: any) {
      setError(err.message || 'Google sign-in failed. Please try again.');
      setLoading(false);
    }
  };

  const handleSendOtp = async () => {
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 10) { setError('Enter a valid 10-digit mobile number.'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/public/otp/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 429) {
          const retryAfter = parseInt(res.headers.get('Retry-After') || '30');
          startCooldown(retryAfter);
          setError(data.error || `Too many requests. Wait ${retryAfter}s.`);
        } else {
          setError(data.error || 'Failed to send OTP.');
        }
        return;
      }
      setPhoneStep('otp');
      setSuccess(`OTP sent to +91 ${digits.slice(-10)}`);
      startCooldown(30);
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.length < 6) { setError('Enter the 6-digit OTP.'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/public/otp/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, otp }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Invalid OTP. Please try again.');
        return;
      }
      if (data.token) {
        const supabase = getSupabase();
        const { error: signInErr } = await supabase.auth.signInWithPassword({
          email: data.email || `${phone.replace(/\D/g, '')}@lexigen.ai`,
          password: data.token,
        });
        if (signInErr) throw signInErr;
      }
      window.location.href = '/dashboard';
    } catch (err: any) {
      setError(err.message || 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailLogin = async () => {
    if (!email || !password) { setError('Enter your email and password.'); return; }
    setLoading(true);
    setError('');
    try {
      const supabase = getSupabase();
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      window.location.href = '/dashboard';
    } catch (err: any) {
      setError(err.message || 'Sign-in failed. Please check your credentials.');
      setLoading(false);
    }
  };

  return (
    <PageErrorBoundary>
      <div className="min-h-screen bg-surface-2 flex items-center justify-center p-4">
        <div className="w-full max-w-md">

          {/* Logo */}
          <div className="text-center mb-8">
            <div
              className="inline-flex items-center justify-center w-12 h-12 rounded-2xl mb-4 shadow-sm"
              style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #0f3460 100%)', border: '1px solid rgba(99,102,241,0.3)' }}
              aria-hidden="true"
            >
              <svg width={22} height={22} viewBox="0 0 18 18" fill="none">
                <path d="M3 3h5v2H5v8h3v2H3V3z" fill="white" fillOpacity="0.9" />
                <path d="M10 3h5v2h-3v3h3v2h-3v3h3v2h-5V3z" fill="#818cf8" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-ink">
              LEXIGEN <span className="text-indigo-600">AI</span>
            </h1>
            <p className="text-[14px] text-ink-muted mt-1">AI-powered legal & business registration</p>
          </div>

          {/* Card */}
          <div className="bg-white rounded-2xl border border-border shadow-card p-6 sm:p-8">
            <h2 className="text-[18px] font-bold text-ink mb-1">Sign in</h2>
            <p className="text-[13px] text-ink-muted mb-6">Access your Lexigen AI dashboard</p>

            {/* Auth Mode Tabs */}
            <div className="flex gap-1 bg-surface-2 rounded-xl p-1 mb-6" role="tablist" aria-label="Sign in method">
              {[
                { id: 'phone', label: 'Phone' },
                { id: 'google', label: 'Google' },
                { id: 'email', label: 'Email' },
              ].map(tab => (
                <button
                  key={tab.id}
                  role="tab"
                  aria-selected={mode === tab.id}
                  onClick={() => { setMode(tab.id as AuthMode); setError(''); setSuccess(''); }}
                  className={`flex-1 py-2 rounded-lg text-[13px] font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai ${
                    mode === tab.id ? 'bg-white text-ink shadow-sm' : 'text-ink-muted hover:text-ink'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Error / Success */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 flex items-start gap-2" role="alert">
                <svg className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <p className="text-[12px] text-red-800 font-medium">{error}</p>
              </div>
            )}
            {success && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-4" role="status">
                <p className="text-[12px] text-green-800 font-medium">{success}</p>
              </div>
            )}

            {/* Phone OTP */}
            {mode === 'phone' && (
              <div className="space-y-4">
                {phoneStep === 'input' ? (
                  <>
                    <div>
                      <label htmlFor="phone-input" className="block text-[12px] font-semibold text-ink-muted mb-1.5">
                        Mobile number
                      </label>
                      <div className="flex gap-2">
                        <div className="flex items-center px-3 bg-surface-2 border border-border rounded-xl text-[13px] text-ink-muted font-medium flex-shrink-0">
                          +91
                        </div>
                        <input
                          id="phone-input"
                          type="tel"
                          inputMode="numeric"
                          placeholder="10-digit mobile number"
                          value={phone}
                          onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                          onKeyDown={e => e.key === 'Enter' && handleSendOtp()}
                          maxLength={10}
                          autoComplete="tel-national"
                          className="flex-1 px-4 py-3 bg-surface-2 border border-border rounded-xl text-[14px] text-ink placeholder-ink-subtle focus:outline-none focus:ring-2 focus:ring-ai focus:border-transparent transition-colors"
                          aria-describedby={error ? 'login-error' : undefined}
                        />
                      </div>
                    </div>
                    <button
                      onClick={handleSendOtp}
                      disabled={loading || phone.replace(/\D/g, '').length < 10}
                      className="w-full py-3 rounded-xl text-[14px] font-semibold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai focus-visible:ring-offset-2"
                      style={{ background: 'linear-gradient(135deg, #4f46e5, #6366f1)' }}
                    >
                      {loading ? 'Sending OTP…' : 'Send OTP'}
                    </button>
                  </>
                ) : (
                  <>
                    <div>
                      <label htmlFor="otp-input" className="block text-[12px] font-semibold text-ink-muted mb-1.5">
                        Enter OTP
                      </label>
                      <input
                        id="otp-input"
                        type="text"
                        inputMode="numeric"
                        placeholder="6-digit OTP"
                        value={otp}
                        onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        onKeyDown={e => e.key === 'Enter' && handleVerifyOtp()}
                        maxLength={6}
                        autoComplete="one-time-code"
                        autoFocus
                        className="w-full px-4 py-3 bg-surface-2 border border-border rounded-xl text-[14px] text-ink placeholder-ink-subtle focus:outline-none focus:ring-2 focus:ring-ai focus:border-transparent tracking-widest text-center"
                        aria-label="One-time password"
                      />
                    </div>
                    <button
                      onClick={handleVerifyOtp}
                      disabled={loading || otp.length < 6}
                      className="w-full py-3 rounded-xl text-[14px] font-semibold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai focus-visible:ring-offset-2"
                      style={{ background: 'linear-gradient(135deg, #4f46e5, #6366f1)' }}
                    >
                      {loading ? 'Verifying…' : 'Verify OTP'}
                    </button>
                    <div className="flex items-center justify-between text-[12px]">
                      <button
                        onClick={() => { setPhoneStep('input'); setOtp(''); setError(''); }}
                        className="text-ink-muted hover:text-ink transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai rounded"
                      >
                        ← Change number
                      </button>
                      <button
                        onClick={handleSendOtp}
                        disabled={resendCooldown > 0 || loading}
                        className="text-ai hover:text-ai-dark transition-colors disabled:text-ink-subtle disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai rounded"
                      >
                        {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend OTP'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Google */}
            {mode === 'google' && (
              <button
                onClick={handleGoogleLogin}
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 py-3 rounded-xl border border-border bg-white hover:bg-surface-2 text-[14px] font-semibold text-ink transition-all disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai focus-visible:ring-offset-2"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {loading ? 'Redirecting…' : 'Continue with Google'}
              </button>
            )}

            {/* Email */}
            {mode === 'email' && (
              <div className="space-y-4">
                <div>
                  <label htmlFor="email-input" className="block text-[12px] font-semibold text-ink-muted mb-1.5">
                    Email address
                  </label>
                  <input
                    id="email-input"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    autoComplete="email"
                    className="w-full px-4 py-3 bg-surface-2 border border-border rounded-xl text-[14px] text-ink placeholder-ink-subtle focus:outline-none focus:ring-2 focus:ring-ai focus:border-transparent transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="password-input" className="block text-[12px] font-semibold text-ink-muted mb-1.5">
                    Password
                  </label>
                  <input
                    id="password-input"
                    type="password"
                    placeholder="Your password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleEmailLogin()}
                    autoComplete="current-password"
                    className="w-full px-4 py-3 bg-surface-2 border border-border rounded-xl text-[14px] text-ink placeholder-ink-subtle focus:outline-none focus:ring-2 focus:ring-ai focus:border-transparent transition-colors"
                  />
                </div>
                <button
                  onClick={handleEmailLogin}
                  disabled={loading || !email || !password}
                  className="w-full py-3 rounded-xl text-[14px] font-semibold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai focus-visible:ring-offset-2"
                  style={{ background: 'linear-gradient(135deg, #4f46e5, #6366f1)' }}
                >
                  {loading ? 'Signing in…' : 'Sign in'}
                </button>
              </div>
            )}
          </div>

          {/* Footer */}
          <p className="text-center text-[12px] text-ink-muted mt-6">
            Don&apos;t have an account?{' '}
            <Link href="/onboarding" className="text-ai font-semibold hover:text-ai-dark transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai rounded">
              Get started →
            </Link>
          </p>
          <p className="text-center text-[11px] text-ink-subtle mt-3">
            By signing in, you agree to our{' '}
            <a href="#" className="underline hover:text-ink-muted transition-colors">Terms of Service</a>
            {' '}and{' '}
            <a href="#" className="underline hover:text-ink-muted transition-colors">Privacy Policy</a>
          </p>
        </div>
      </div>
    </PageErrorBoundary>
  );
}
